import React, { useRef } from 'react';

import { BRIDGE_KEYS, LayerDataBridge } from '../../../../shared/utils/layer-data-bridge';
import { useLayerManager } from '../../../../shared/hooks/use-layer-manager';
import { useNicknameLayerData } from '../../components/car-nickname-form/hooks/useNicknameLayerData';
import { ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';

export type NicknameLayerData = {
  title: React.ReactNode;
  content: React.ReactNode;
  onCloseAttempt: () => void;
};

type NicknameLayerRenderFn = (
  state: { dataBridge: LayerDataBridge<NicknameLayerData> },
  close: any,
) => JSX.Element;

export const useNicknameLayer = (
  active: boolean,
  onClose: () => void,
  onDiscardClose: (closeFn: () => void) => void,
  renderFn: NicknameLayerRenderFn,
): void => {
  const layerManager = useLayerManager();
  const hasOpenedRef = useRef(false);
  const layerHandleRef = useRef<{ close: () => void } | null>(null);
  const changesRef = useRef(false);
  const bridge = LayerDataBridge.getInstance<NicknameLayerData>(BRIDGE_KEYS.NICKNAME);

  const data = useNicknameLayerData(
    () => layerHandleRef.current?.close(),
    () => { changesRef.current = true; },
  );

  React.useEffect(() => {
    if (!active) {
      bridge.setData(null);
      changesRef.current = false;
      return;
    }
    bridge.setData({
      title: data.title,
      content: data.content,
      onCloseAttempt: () => {
        if (changesRef.current) onDiscardClose(() => layerHandleRef.current?.close());
        else layerHandleRef.current?.close();
      },
    });
  }, [active]);

  React.useEffect(() => {
    if (active && !hasOpenedRef.current && layerManager) {
      hasOpenedRef.current = true;
      changesRef.current = false;
      bridge.setData({
        title: data.title,
        content: data.content,
        onCloseAttempt: () => {
          if (changesRef.current) onDiscardClose(() => layerHandleRef.current?.close());
          else layerHandleRef.current?.close();
        },
      });
      layerHandleRef.current = layerManager.openInteractionLayer(
        renderFn,
        { dataBridge: bridge },
        {
          size: 'E' as any,
          shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
          onClose: (_state: any, _response: any) => {
            hasOpenedRef.current = false;
            changesRef.current = false;
            bridge.setData(null);
            onClose();
          },
        },
      );
    }
    if (!active) {
      hasOpenedRef.current = false;
    }
  }, [active]);
};
